import { useMemo } from 'react';

const UNAVAILABLE_STORAGE_STATES = new Set(['retired', 'unavailable', 'retrieval_failed']);
const UNAVAILABLE_SUPPORTING_FILE_LABEL = 'Evidence unavailable - replace and verify again';

export function isSupportingFileUnavailable(file) {
  const assessment = file?.retention_assessment || {};
  return Boolean(
    file?.file_unavailable ||
    file?.bytes_unavailable ||
    assessment?.file_unavailable ||
    UNAVAILABLE_STORAGE_STATES.has(String(file?.storage_availability || '').toLowerCase()) ||
    UNAVAILABLE_STORAGE_STATES.has(String(assessment?.storage_availability || '').toLowerCase())
  );
}

export function computeComplianceWorkflow({
  requirementKey,
  evidenceFiles = [],
  checkRecord = null,
  canonicalStatus = null,
  statusUnavailable = false,
  isAdminView = true,
}) {
  const isRTW = requirementKey === 'right_to_work';
  const isDBS = requirementKey === 'dbs';
  const isPOA = requirementKey === 'proof_of_address';
  const isIdentity = requirementKey === 'identity';
  const isRTWOrDBS = isRTW || isDBS;
  const hasCheckStage = true;
  const minRequiredFiles = isPOA ? 2 : 1;

  const unavailableEvidence = evidenceFiles.filter(isSupportingFileUnavailable);
  const hasAnyEvidence = evidenceFiles.length > 0;
  const hasEvidence = evidenceFiles.length >= minRequiredFiles;
  const acceptedFiles = evidenceFiles.filter(
    (f) =>
      !isSupportingFileUnavailable(f) &&
      (
        f.verified ||
        f.status === 'verified' ||
        f.status === 'accepted' ||
        f.status === 'approved'
      ),
  );
  const hasAcceptedEvidence = acceptedFiles.length >= minRequiredFiles;
  const hasRejectedEvidence = evidenceFiles.some((f) => f.status === 'rejected');
  const allEvidenceReviewed =
    hasEvidence &&
    evidenceFiles.every(
      (f) =>
        f.verified ||
        f.status === 'verified' ||
        f.status === 'accepted' ||
        f.status === 'rejected' ||
        f.verification_stamp,
    );

  const hasCheck = !!checkRecord;
  // A stored verified outcome is historical evidence of an employer decision,
  // not enough to mark time-limited RTW fully complete without follow-up.
  // The server remains the only authority for actual work/shift clearance.
  const rtwTimeLimitedRecorded = isRTW && hasCheck &&
    ['verified', 'follow_up_required'].includes(checkRecord.outcome) &&
    checkRecord.is_indefinite !== true;
  const rtwDue = checkRecord?.follow_up_due_at;
  const rtwDueDate = rtwDue ? new Date(rtwDue) : null;
  const rtwDueInvalid = Boolean(rtwDue && Number.isNaN(rtwDueDate.getTime()));
  const rtwDueOverdue = Boolean(rtwDueDate) && !rtwDueInvalid && (
    typeof rtwDue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rtwDue)
      ? rtwDue < new Date().toISOString().slice(0, 10)
      : rtwDueDate < new Date()
  );
  const rtwRestrictionsMissing = rtwTimeLimitedRecorded &&
    checkRecord.restrictions_reviewed !== true &&
    !String(checkRecord.restrictions || '').trim();
  const rtwFollowUpNeedsAction = Boolean(rtwTimeLimitedRecorded && (
    checkRecord.follow_up_required !== true ||
    !rtwDue || rtwDueInvalid || rtwDueOverdue || rtwRestrictionsMissing
  ));
  const rawCheckVerified = checkRecord?.outcome === 'verified' ||
    (isRTW && checkRecord?.outcome === 'follow_up_required');
  const canonicalNormalized = canonicalStatus ? String(canonicalStatus).toLowerCase() : null;
  // For employment checks, only the backend's authoritative readiness
  // result may paint a card green. A legacy check row cannot clear itself.
  // A document badge or historical verified outcome must never replace the
  // authoritative current employer decision, for ANY of the four check types.
  const checkVerified = Boolean(hasCheck && rawCheckVerified &&
    canonicalNormalized === 'verified' && !statusUnavailable && !rtwFollowUpNeedsAction);
  const checkFailed = checkRecord?.outcome === 'failed';

  const proofDocumentId =
    checkRecord?.proof_document_id || checkRecord?.evidence_document_id || null;
  // A linked accepted source is already present in the tenant-scoped
  // compliance-file evidence row. Reuse that same ID if the check-row
  // proof projection is omitted; never match on filename or another worker.
  const proofDocument = proofDocumentId
    ? [checkRecord?.proof_document, checkRecord?.evidence_document, ...acceptedFiles]
      .find(file => file && (file.id || file.file_id) === proofDocumentId) || null
    : null;
  const linkedProofUnavailable = Boolean(proofDocumentId && proofDocument && isSupportingFileUnavailable(proofDocument));
  const linkedEvidenceUnavailable = Boolean(checkRecord?.evidence_document && isSupportingFileUnavailable(checkRecord.evidence_document));
  const supportingFileUnavailable = Boolean(
    unavailableEvidence.length > 0 ||
    linkedProofUnavailable ||
    linkedEvidenceUnavailable ||
    checkRecord?.supporting_file_unavailable ||
    checkRecord?.linked_proof_unavailable ||
    checkRecord?.linked_evidence_unavailable
  );
  const hasProof = Boolean(proofDocumentId && proofDocument);
  const proofAvailable = hasProof && !linkedProofUnavailable;

  const reviewDueAt =
    checkRecord?.review_due_at || checkRecord?.next_recheck_date || null;
  const isOverdue = reviewDueAt ? new Date(reviewDueAt) < new Date() : false;

  const isIndefinite = checkRecord?.is_indefinite || false;
  const permissionEndDate = checkRecord?.permission_end_date || null;
  const followUpRequired = checkRecord?.follow_up_required || false;
  const followUpDueAt = checkRecord?.follow_up_due_at || null;
  const isExpired = permissionEndDate
    ? new Date(permissionEndDate) < new Date()
    : false;
  const followUpOverdue =
    followUpRequired && followUpDueAt
      ? new Date(followUpDueAt) < new Date()
      : false;

  const isUpdateServiceCheck =
    checkRecord?.method === 'dbs_update_service_check';
  const proofRequired = isRTW || (isDBS && isUpdateServiceCheck);
  const hasProofStep = !isPOA && isRTWOrDBS && (proofRequired || hasProof);

  let currentStep = 1;
  const uploadActionLabel = isPOA
    ? (hasAnyEvidence ? 'Add second address document' : 'Add 2 address documents')
    : isIdentity
      ? 'Add ID document'
      : 'Upload Evidence';
  let stepLabel = uploadActionLabel;

  if (!hasEvidence) {
    currentStep = 1;
    stepLabel = uploadActionLabel;
  } else if (!hasAcceptedEvidence) {
    currentStep = 2;
    stepLabel = supportingFileUnavailable ? UNAVAILABLE_SUPPORTING_FILE_LABEL : 'Review Evidence';
  } else if (!hasCheck || !checkVerified) {
    currentStep = 3;
    stepLabel = isIdentity ? 'Complete formal identity check'
      : isPOA ? 'Review address check' : 'Record / review check';
  } else if (hasProofStep && !proofAvailable) {
    currentStep = 4;
    stepLabel = proofRequired
      ? 'Upload Proof of Check'
      : 'Upload Proof (Recommended)';
  } else {
    currentStep = 5;
    stepLabel = 'Final Status';
  }

  const isKeyRequirement =
    requirementKey === 'dbs' ||
    requirementKey === 'right_to_work' ||
    requirementKey === 'identity' ||
    requirementKey === 'proof_of_address';
  const checkRowVerified = checkVerified;
  const evidenceAndProofReady = hasAcceptedEvidence && !supportingFileUnavailable &&
    (!proofRequired || proofAvailable);

  let finalStatus = 'pending';
  let finalStatusLabel = 'Pending';

  if (isKeyRequirement && checkRowVerified && evidenceAndProofReady) {
    finalStatus = 'verified';
    finalStatusLabel = 'Verified';
  } else if (!hasEvidence) {
    if (isPOA && hasAnyEvidence) {
      finalStatus = 'incomplete';
      finalStatusLabel = '1 of 2 address documents uploaded';
    } else {
      finalStatus = 'pending';
      finalStatusLabel = isPOA ? '2 address documents required' : 'Missing';
    }
  } else if (!hasAcceptedEvidence && hasRejectedEvidence) {
    finalStatus = 'failed';
    finalStatusLabel = 'Rejected / action required';
  } else if (!hasAcceptedEvidence) {
    finalStatus = supportingFileUnavailable ? 'incomplete' : 'pending';
    finalStatusLabel = supportingFileUnavailable ? UNAVAILABLE_SUPPORTING_FILE_LABEL : 'Awaiting admin review';
  } else if (!hasCheck) {
    finalStatus = 'pending';
    finalStatusLabel = 'Evidence accepted - check required';
  } else if (checkFailed) {
    finalStatus = 'failed';
    finalStatusLabel = 'Rejected / action required';
  } else if (!checkVerified) {
    finalStatus = 'pending';
    finalStatusLabel = 'Awaiting admin review';
  } else if (proofRequired && !proofAvailable) {
    // The formal decision may have been recorded, but missing compulsory
    // source proof must never be displayed as current clearance.
    finalStatus = 'incomplete';
    finalStatusLabel = 'Cannot assess - required proof missing';
  } else if (isDBS && isOverdue) {
    finalStatus = 'overdue';
    finalStatusLabel = 'Overdue - Recheck Required';
  } else if (isRTW && isExpired) {
    finalStatus = 'expired';
    finalStatusLabel = 'Expired';
  } else if (isRTW && followUpOverdue) {
    finalStatus = 'follow_up_overdue';
    finalStatusLabel = 'Follow-up Overdue';
  } else if (checkVerified && (proofAvailable || !proofRequired)) {
    finalStatus = 'verified';
    finalStatusLabel = 'Verified';
  }

  if (statusUnavailable) {
    finalStatus = 'pending';
    finalStatusLabel = 'Unavailable';
  } else if (canonicalStatus) {
    const normalized = String(canonicalStatus).toLowerCase();
    const hardFailureOverride = [
      'failed',
      'rejected',
      'expired',
      'revoked',
    ].includes(normalized);
    const isPendingLike = [
      'pending',
      'awaiting_check',
      'awaiting_verification',
      'evidence_under_review',
      'check_in_progress',
      'check_required',
      'proof_required',
      'reupload_required',
      'partially_verified',
      'incomplete',
      'missing',
      'not_started',
    ].includes(normalized);

    if (isKeyRequirement && checkRowVerified && evidenceAndProofReady) {
      if (hardFailureOverride) {
        finalStatus = normalized === 'expired' ? 'expired' : 'failed';
        finalStatusLabel = normalized === 'expired' ? 'Expired' : 'Rejected / action required';
      }
    } else if (normalized === 'verified' && checkRowVerified && evidenceAndProofReady) {
      finalStatus = 'verified';
      finalStatusLabel = 'Verified';
    } else if (hardFailureOverride) {
      finalStatus = 'failed';
      finalStatusLabel = 'Rejected / action required';
    } else if (normalized === 'incomplete' || normalized === 'missing' || normalized === 'not_started') {
      finalStatus = 'incomplete';
      finalStatusLabel = 'Incomplete';
    } else if (isPendingLike) {
      finalStatus = 'pending';
      finalStatusLabel = 'Awaiting review';
    }
  }

  if (supportingFileUnavailable) {
    finalStatus = 'incomplete';
    finalStatusLabel = UNAVAILABLE_SUPPORTING_FILE_LABEL;
  } else if (isRTW && rtwFollowUpNeedsAction && !statusUnavailable && hasAcceptedEvidence) {
    // Never paint an operationally incomplete RTW review green just because a
    // historical check row and the section-level canonical status say verified.
    finalStatus = 'pending';
    finalStatusLabel = 'RTW follow-up action required';
  }

  const safeguards = {
    noEvidenceButCheckExists: !hasEvidence && hasCheck,
    proofMissingButCheckVerified: checkVerified && proofRequired && !hasProof,
    supportingFileUnavailable,
  };

  let nextAction = null;
  if (supportingFileUnavailable && isAdminView) {
    nextAction = {
      label: linkedProofUnavailable ? 'Replace Proof' : 'Re-upload Evidence',
      type: linkedProofUnavailable ? 'upload_proof' : 'upload_evidence',
    };
  } else if (!hasEvidence) {
    nextAction = {
      label: uploadActionLabel,
      type: 'upload_evidence',
    };
  } else if (!hasAcceptedEvidence && isAdminView) {
    nextAction = { label: 'Review Evidence', type: 'review_evidence' };
  } else if (!hasCheck && isAdminView) {
    nextAction = {
      label: (isPOA || isIdentity) ? 'Record interview confirmation' : 'Record Check',
      type: 'record_check',
      disabled: !hasAcceptedEvidence,
    };
  } else if (hasProofStep && proofRequired && !proofAvailable && isAdminView) {
    nextAction = { label: 'Upload Proof', type: 'upload_proof' };
  } else if (isRTW && rtwFollowUpNeedsAction && isAdminView) {
    nextAction = { label: 'Set or review RTW follow-up', type: 'record_check' };
  } else if (isRTW && followUpOverdue && isAdminView) {
    nextAction = { label: 'Resolve Follow-up', type: 'resolve_followup' };
  } else if (isDBS && isOverdue && isAdminView) {
    // Preserve the existing employer recheck action for overdue DBS records.
    nextAction = { label: 'Record Recheck', type: 'record_check' };
  } else if (hasCheck && !checkVerified && hasAcceptedEvidence && isAdminView) {
    // A stamped identity file is not a complete employer identity decision.
    // Keep a usable manager action for the separate structured confirmation.
    const label = isIdentity ? 'Complete formal Identity Check'
      : isPOA ? 'Review address verification'
      : isDBS ? 'Review DBS Check' : 'Review Right to Work Check';
    nextAction = { label, type: 'record_check' };
  }

  const steps = [
    {
      id: 1,
      label: isPOA ? '2 documents' : isIdentity ? 'ID document' : 'Evidence',
      complete: hasEvidence && unavailableEvidence.length === 0,
      active: currentStep === 1,
      locked: false,
      optional: false,
    },
    {
      id: 2,
      label: 'Review',
      complete: hasAcceptedEvidence,
      active: currentStep === 2,
      locked: !hasAnyEvidence,
      optional: false,
    },
    {
      id: 3,
      label: (isPOA || isIdentity) ? 'Interview' : 'Check',
      complete: hasCheck && checkVerified && hasAcceptedEvidence && !supportingFileUnavailable,
      active: currentStep === 3,
      locked: !hasAcceptedEvidence,
      optional: false,
    },
    {
      id: 4,
      label: 'Proof',
      complete: proofAvailable,
      active: currentStep === 4,
      locked: !hasCheck,
      optional: !proofRequired,
    },
    {
      id: 5,
      label: 'Status',
      complete: finalStatus === 'verified',
      active: currentStep === 5,
      locked: false,
      optional: false,
    },
  ];

  return {
    currentStep,
    stepLabel,
    steps,
    isRTWOrDBS,
    hasCheckStage,
    hasProofStep,
    isRTW,
    isDBS,
    isPOA,
    isIdentity,
    minRequiredFiles,
    hasAnyEvidence,
    hasEvidence,
    hasAcceptedEvidence,
    hasRejectedEvidence,
    allEvidenceReviewed,
    acceptedFiles,
    unavailableEvidence,
    hasCheck,
    checkVerified,
    checkFailed,
    proofDocumentId,
    proofDocument,
    hasProof,
    proofAvailable,
    proofRequired,
    linkedProofUnavailable,
    linkedEvidenceUnavailable,
    supportingFileUnavailable,
    reviewDueAt,
    isOverdue,
    permissionEndDate,
    isIndefinite,
    isExpired,
    followUpDueAt,
    followUpOverdue,
    rtwFollowUpNeedsAction,
    finalStatus,
    finalStatusLabel,
    nextAction,
    safeguards,
  };
}

/**
 * useComplianceWorkflow
 *
 * Derives the complete workflow state for a single compliance requirement
 * from raw compliance-file API data. Pure computation — no side-effects,
 * no API calls.
 *
 * @param {Object} params
 * @param {string}  params.requirementKey  'dbs' | 'right_to_work' | 'identity' | 'proof_of_address'
 * @param {Array}   params.evidenceFiles   Active evidence files from the evidence row
 * @param {Object}  params.checkRecord     check_data from check row (or null)
 * @param {boolean} params.isAdminView     Whether rendered in admin context
 */
export function useComplianceWorkflow({
  requirementKey,
  evidenceFiles = [],
  checkRecord = null,
  canonicalStatus = null,
  statusUnavailable = false,
  isAdminView = true,
}) {
  return useMemo(() => computeComplianceWorkflow({
    requirementKey,
    evidenceFiles,
    checkRecord,
    canonicalStatus,
    statusUnavailable,
    isAdminView,
  }), [requirementKey, evidenceFiles, checkRecord, canonicalStatus, statusUnavailable, isAdminView]);
}
